import React, { useEffect, useState } from 'react';
import { analytics as analyticsApi, extractError } from '../services/api';
import { useToast } from '../store/ToastContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const RANGES = [
  { value: '7d',  label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
];

const PIE_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#14b8a6','#f97316'];

function KPICard({ icon, label, value, sub, color }) {
  const colorMap = {
    blue:   'from-blue-500 to-blue-600',
    green:  'from-green-500 to-green-600',
    teal:   'from-teal-500 to-teal-600',
    orange: 'from-orange-400 to-orange-500',
    purple: 'from-purple-500 to-purple-600',
    red:    'from-red-500 to-red-600',
  };
  return (
    <div className="card p-5">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorMap[color] || colorMap.blue} flex items-center justify-center text-white text-lg flex-shrink-0`}>
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-2xl font-bold text-gray-900 leading-tight">{value ?? '—'}</div>
          <div className="text-sm text-gray-500 mt-0.5">{label}</div>
          {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title }) {
  return <h3 className="text-base font-semibold text-gray-900 mb-4">{title}</h3>;
}

export function FleetAnalytics() {
  const toast = useToast();
  const [range, setRange]   = useState('30d');
  const [kpis, setKpis]     = useState(null);
  const [fuel, setFuel]     = useState([]);
  const [perf, setPerf]     = useState([]);
  const [byStatus, setByStatus] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      analyticsApi.kpis(range),
      analyticsApi.fuelEfficiency(range),
      analyticsApi.driverPerformance(range),
      analyticsApi.tripsByStatus(range),
    ])
      .then(([k, f, p, s]) => {
        setKpis(k);
        setFuel(Array.isArray(f) ? f : f?.data || []);
        setPerf(Array.isArray(p) ? p : p?.data || []);
        setByStatus(Array.isArray(s) ? s : s?.data || []);
      })
      .catch(e => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  }, [range]);

  const fuelChartData = fuel.map(f => ({
    name: f.unit_number,
    mpg: parseFloat(f.avg_mpg) || 0,
    gallons: parseFloat(f.total_gallons) || 0,
    cost: parseFloat(f.total_cost) || 0,
  }));

  const perfChartData = perf.slice(0, 8).map(p => ({
    name: p.driver_name?.split(' ')[0] || '—',
    completed: parseInt(p.trips_completed) || 0,
    onTime: parseFloat(p.on_time_pct) || 0,
    violations: parseInt(p.hos_violations) || 0,
  }));

  const statusChartData = byStatus.map((s, i) => ({
    name: s.status?.replace(/_/g, ' ') || s.name?.replace(/_/g, ' '),
    value: parseInt(s.count) || 0,
    color: PIE_COLORS[i % PIE_COLORS.length],
  }));

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Fleet Analytics</h1>
            <p className="text-sm text-gray-500">Performance metrics and insights</p>
          </div>
          <div className="flex gap-2">
            {RANGES.map(r => (
              <button key={r.value} onClick={() => setRange(r.value)}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${range === r.value ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="p-6 space-y-6">
          {/* KPI Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard icon="🗺️" label="Total Trips"         value={kpis?.total_trips}           color="blue"   />
            <KPICard icon="✅" label="Completed"            value={kpis?.completed}             color="green"  />
            <KPICard icon="📏" label="Miles Driven"         value={kpis?.total_miles > 0 ? `${Math.round(kpis.total_miles).toLocaleString()} mi` : '0'} color="purple" />
            <KPICard icon="⛽" label="Avg Fleet MPG"        value={kpis?.avg_mpg > 0 ? `${kpis.avg_mpg}` : '—'} sub="miles per gallon" color="teal" />
            <KPICard icon="🔴" label="Active Trips"         value={kpis?.active}                color="orange" />
            <KPICard icon="❌" label="Cancelled"            value={kpis?.cancelled}             color="red"    />
            <KPICard icon="⏱️" label="On-Time Rate"         value={kpis?.on_time_pct != null ? `${kpis.on_time_pct}%` : '—'} color="green" />
            <KPICard icon="🚛" label="Vehicles In Use"      value={kpis?.vehicles_in_use}       color="blue"   sub={`of ${kpis?.fleet_vehicles} total`} />
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Trips by Status Pie */}
            <div className="card p-5">
              <SectionHeader title="Trips by Status" />
              {statusChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={statusChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`} labelLine>
                      {statusChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [v, 'Trips']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-56 flex items-center justify-center text-gray-400 text-sm">No data</div>
              )}
            </div>

            {/* Fuel Efficiency Bar */}
            <div className="card p-5">
              <SectionHeader title="Vehicle Fuel Efficiency (MPG)" />
              {fuelChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={fuelChartData} margin={{ left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`${v} mpg`, 'Avg MPG']} />
                    <Bar dataKey="mpg" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-56 flex items-center justify-center text-gray-400 text-sm">No fuel data</div>
              )}
            </div>
          </div>

          {/* Driver Performance Chart */}
          {perfChartData.length > 0 && (
            <div className="card p-5">
              <SectionHeader title="Driver Performance — Trips Completed" />
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={perfChartData} margin={{ left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="completed" fill="#10b981" name="Completed" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="violations" fill="#ef4444" name="HOS Violations" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Driver Performance Table */}
          {perf.length > 0 && (
            <div className="card overflow-hidden">
              <div className="card-header">
                <h3 className="font-semibold text-gray-900">Driver Performance Detail</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Driver','Trips Total','Completed','Miles','On-Time %','HOS Violations'].map(h => (
                        <th key={h} className="table-header">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {perf.map((d, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="table-cell font-medium text-gray-900">{d.driver_name}</td>
                        <td className="table-cell">{d.trips_total || 0}</td>
                        <td className="table-cell text-green-600 font-medium">{d.trips_completed || 0}</td>
                        <td className="table-cell">{d.total_miles ? `${Math.round(parseFloat(d.total_miles)).toLocaleString()} mi` : '—'}</td>
                        <td className="table-cell">
                          <span className={`font-semibold ${(parseFloat(d.on_time_pct) || 0) >= 90 ? 'text-green-600' : (parseFloat(d.on_time_pct) || 0) >= 75 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {d.on_time_pct ? `${parseFloat(d.on_time_pct).toFixed(1)}%` : '—'}
                          </span>
                        </td>
                        <td className="table-cell">
                          <span className={`font-semibold ${parseInt(d.hos_violations) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {d.hos_violations || 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Fuel Cost Table */}
          {fuel.length > 0 && (
            <div className="card overflow-hidden">
              <div className="card-header">
                <h3 className="font-semibold text-gray-900">Fuel Cost by Vehicle</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Unit #','Make / Model','Avg MPG','Gallons Used','Fuel Cost','Fill Count'].map(h => (
                        <th key={h} className="table-header">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {fuel.map((v, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="table-cell font-bold text-gray-900">{v.unit_number}</td>
                        <td className="table-cell">{v.make} {v.model}</td>
                        <td className="table-cell">{parseFloat(v.avg_mpg || 0).toFixed(2)}</td>
                        <td className="table-cell">{parseFloat(v.total_gallons || 0).toFixed(1)}</td>
                        <td className="table-cell font-semibold text-gray-900">
                          {v.total_cost ? `$${parseFloat(v.total_cost).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
                        </td>
                        <td className="table-cell">{v.fill_count || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
